import { useState } from "react";
import { Zap, ArrowRight, CheckCircle2, Users, Loader2 } from "lucide-react";

// -------------------------------------------------------
// IMPORTANT: Replace these with your Google Form field IDs
// Instructions below in the code comments
// -------------------------------------------------------
const GOOGLE_FORM_ACTION = "YOUR_GOOGLE_FORM_ACTION_URL";
const FIELD_IDS = {
  name: "entry.YOUR_NAME_FIELD_ID",
  email: "entry.YOUR_EMAIL_FIELD_ID",
  company: "entry.YOUR_COMPANY_FIELD_ID",
  role: "entry.YOUR_ROLE_FIELD_ID",
  phone: "entry.YOUR_PHONE_FIELD_ID",
  wishlist: "entry.YOUR_WISHLIST_FIELD_ID",
};
// -------------------------------------------------------

const ROLES = [
  "GP (General Partner)",
  "LP (Limited Partner)",
  "VC Analyst",
  "Associate",
  "Principal",
  "Venture Partner",
  "Angel Investor",
  "Other",
];

export default function WaitlistPage({ onEnter }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    wishlist: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalSignups] = useState(Math.floor(Math.random() * 50) + 120); // Replace with real count later

  const isValid = form.name && form.email && form.company && form.role;

  const handleSubmit = async () => {
    if (!isValid) return;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append(FIELD_IDS.name, form.name);
      formData.append(FIELD_IDS.email, form.email);
      formData.append(FIELD_IDS.company, form.company);
      formData.append(FIELD_IDS.role, form.role);
      formData.append(FIELD_IDS.phone, form.phone);
      formData.append(FIELD_IDS.wishlist, form.wishlist);

      await fetch(GOOGLE_FORM_ACTION, {
        method: "POST",
        mode: "no-cors",
        body: formData,
      });
    } catch (e) {
      // no-cors always throws, but data is submitted
    }

    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4 text-center relative overflow-hidden">
        {/* Background text */}
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] pointer-events-none select-none">
          <h1 className="text-[20vw] font-black">BINOS</h1>
        </div>

        <div className="relative z-10 max-w-lg">
          <div className="w-24 h-24 bg-green-50 rounded-3xl flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-12 h-12 text-green-500" />
          </div>
          <h2 className="text-5xl font-black tracking-tighter text-gray-900 mb-4">
            You're on the list!
          </h2>
          <p className="text-gray-500 font-medium text-lg mb-8">
            Welcome to Binos, {form.name.split(" ")[0]}. We'll reach out to{" "}
            <span className="font-black text-gray-900">{form.email}</span> when
            your access is ready.
          </p>

          {/* Total signups */}
          <div className="inline-flex items-center gap-3 bg-gray-50 px-6 py-4 rounded-2xl mb-10">
            <Users className="w-5 h-5 text-red-600" />
            <p className="font-black text-gray-900">
              <span className="text-red-600">{totalSignups + 1}</span> people
              are ahead of you in line
            </p>
          </div>

          <p className="text-sm text-gray-400 font-medium">
            In the meantime, share Binos with other VCs you know 🚀
          </p>

          {/* Share button */}
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              alert("Link copied to clipboard!");
            }}
            className="mt-6 px-8 py-4 bg-gray-900 text-white font-black rounded-2xl text-sm hover:bg-black transition"
          >
            Copy Invite Link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row overflow-hidden">
      {/* Left Panel */}
      <div className="lg:w-1/2 bg-gray-900 flex flex-col justify-between p-12 lg:p-16 relative overflow-hidden">
        {/* Background Zap */}
        <Zap className="absolute -right-16 -bottom-16 w-96 h-96 text-white opacity-5" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <Zap className="w-8 h-8 text-red-600" />
            <span className="font-black text-white text-2xl tracking-tighter">Binos</span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-black text-white tracking-tighter leading-none mb-6">
            Venture Intelligence.<br />
            <span className="text-red-600">Reimagined.</span>
          </h1>

          <p className="text-gray-400 font-medium text-lg leading-relaxed max-w-md">
            The autonomous VC operating system. Monitor fund health, analyze pitch decks with AI, and make faster investment decisions.
          </p>
        </div>

        {/* Features */}
        <div className="relative z-10 space-y-4 mt-12">
          {[
            "AI-powered pitch deck analysis",
            "Real-time portfolio health monitoring",
            "Fund efficiency calculator",
            "LP reporting automation",
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-3 h-3 text-white" />
              </div>
              <p className="text-gray-300 font-medium text-sm">{f}</p>
            </div>
          ))}
        </div>

        {/* Signup count */}
        <div className="relative z-10 mt-12 flex items-center gap-3">
          <div className="flex -space-x-2">
            {["MV", "SK", "AR", "JP"].map((init, i) => (
              <div
                key={i}
                className="w-8 h-8 rounded-full bg-red-600 border-2 border-gray-900 flex items-center justify-center text-[9px] font-black text-white"
              >
                {init}
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-sm font-medium">
            <span className="text-white font-black">{totalSignups}+</span> VCs already on the waitlist
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-black tracking-tighter text-gray-900 mb-2">
            Request Early Access
          </h2>
          <p className="text-gray-400 font-medium text-sm mb-8">
            Join the waitlist and be first to experience Binos.
          </p>

          <div className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Full Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Rahul Kumar"
                className="w-full mt-2 p-4 bg-gray-50 border-2 border-transparent focus:border-red-600 rounded-2xl outline-none font-bold text-gray-900 text-sm transition"
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Work Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="melvin@vcfund.com"
                className="w-full mt-2 p-4 bg-gray-50 border-2 border-transparent focus:border-red-600 rounded-2xl outline-none font-bold text-gray-900 text-sm transition"
              />
            </div>

            {/* Company */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Fund / Company *</label>
              <input
                type="text"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Sequoia Capital"
                className="w-full mt-2 p-4 bg-gray-50 border-2 border-transparent focus:border-red-600 rounded-2xl outline-none font-bold text-gray-900 text-sm transition"
              />
            </div>

            {/* Role */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Your Role *</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full mt-2 p-4 bg-gray-50 border-2 border-transparent focus:border-red-600 rounded-2xl outline-none font-bold text-gray-900 text-sm transition"
              >
                <option value="">Select your role...</option>
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>

            {/* Phone */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Phone Number</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 (555) 000-0000"
                className="w-full mt-2 p-4 bg-gray-50 border-2 border-transparent focus:border-red-600 rounded-2xl outline-none font-bold text-gray-900 text-sm transition"
              />
            </div>

            {/* Wishlist */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">What would you love to see in Binos?</label>
              <textarea
                value={form.wishlist}
                onChange={(e) => setForm({ ...form, wishlist: e.target.value })}
                placeholder="Tell us what features would make your life easier..."
                rows={3}
                className="w-full mt-2 p-4 bg-gray-50 border-2 border-transparent focus:border-red-600 rounded-2xl outline-none font-bold text-gray-900 text-sm transition resize-none"
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!isValid || loading}
              className="w-full py-5 bg-red-600 text-white font-black rounded-2xl shadow-xl shadow-red-100 hover:bg-red-700 transition flex items-center justify-center gap-3 text-sm uppercase tracking-widest disabled:opacity-30 mt-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Join the Waitlist
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            <p className="text-center text-[10px] text-gray-400 font-medium">
              No spam, ever. We'll only reach out when your access is ready.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
